/** @jsxImportSource @emotion/react */
import { css, useTheme } from '@emotion/react';
import { motion } from 'framer-motion';
import list1Img from 'static/class/section1_list1.png';
import list2Img from 'static/class/section1_list2.png';
import list3Img from 'static/class/section1_list3.png';

const Section1 = () => {
  const { mq } = useTheme();
  const Section1Style = css`
    display: flex;
    justify-content: center;
    .container {
      max-width: 1200px;
      padding: 46px 0px 227px;
      margin: 0 auto;
      > .title {
        color: #000;
        font-weight: 700;
        font-size: 68px;
        line-height: 50px;
        text-align: center;
        line-height: 96px;
        span {
          color: #6079ff;
        }
      }
      .list {
        display: flex;
        flex-direction: column;
        gap: 155px;
        margin-top: 100px;
        width: 1000px;
        li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-direction: row-reverse;
          &:nth-of-type(2) {
            flex-direction: row;
          }
          .text {
            padding: 0px 40px;
            .icon {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 24px;
              font-weight: 700;
              line-height: 30px;
              color: #6079ff;
            }
            .title {
              font-size: 40px;
              font-weight: 800;
              line-height: 60px;
              span {
                color: #6079ff;
              }
            }
            .desc {
              margin-top: 10px;
              color: #444;
              font-size: 20px;
              font-weight: 400;
              line-height: 30px;
            }
          }
          .img {
            width: 480px;
            flex-shrink: 0;
            border-radius: 32px;
            box-shadow: rgba(0, 0, 0, 0.1) 0px 12px 40px 0px;
            img {
              width: 100%;
            }
          }
        }
      }
    }
    ${mq.mobile} {
      .container {
        padding: 20px 20px 96px;
        width: 100%;
        > .title {
          font-size: 32px;
          font-weight: 800;
          line-height: 45px; /* 140.625% */
        }
        .list {
          gap: 64px;
          margin-top: 64px;
          width: auto;
          li {
            align-items: flex-start;
            flex-direction: column-reverse;
            &:nth-of-type(2) {
              flex-direction: column-reverse;
            }
            .text {
              padding: 0px;
              text-align: left;
              margin-bottom: 24px;
              .icon {
                font-size: 14px;
                font-weight: 600;
                line-height: 20px;
                svg {
                  width: 20px;
                  height: 20px;
                }
              }
              .title {
                font-size: 28px;
                line-height: 40px;
              }
              .desc {
                font-size: 16px;
                font-weight: 400;
                line-height: 24px;
              }
            }
            .img {
              width: 100%;
            }
          }
        }
      }
    }
  `;

  const scaleVariants = {
    offscreen: {
      scale: 0.7,
      opacity: 0,
    },
    onscreen: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const yVariants = {
    offscreen: {
      y: 100,
      opacity: 0,
    },
    onscreen: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div css={Section1Style}>
      <div className="container">
        <h2 className="title">
          <span>몰파이</span>를
          <br /> 추천합니다!
        </h2>
        <ul className="list">
          <motion.li
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ amount: 0.4 }}
          >
            <motion.div className="img" variants={scaleVariants}>
              <img
                src={list1Img}
                alt="모임 구성원이라면 하나의 아이디로 누구나 | 이미지"
                title="모임 구성원이라면 하나의 아이디로 누구나"
              />
            </motion.div>
            <motion.div className="text" variants={yVariants}>
              <h3 className="title">
                모임 구성원이라면
                <br />
                <span>하나의 아이디</span>로 누구나
              </h3>
              <p className="desc">
                모임 장 보기, 늘 총무만 구매하는 <br />
                상황이 불합리하다 느껴진다면
              </p>
            </motion.div>
          </motion.li>
          <motion.li
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ amount: 0.4 }}
          >
            <motion.div className="img" variants={scaleVariants}>
              <img
                src={list2Img}
                alt="구매한 내역은 누구나 조회 가능 | 이미지"
                title="구매한 내역은 누구나 조회 가능"
              />
            </motion.div>
            <motion.div className="text" variants={yVariants}>
              <h3 className="title">
                구매한 내역은
                <br />
                <span>누구나</span> 조회 가능!
              </h3>
              <p className="desc">
                모임에서 물건을 함께 구매하고 <br />
                투명하게 내역을 관리하고 싶다면
              </p>
            </motion.div>
          </motion.li>
          <motion.li
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ amount: 0.4 }}
          >
            <motion.div className="img" variants={scaleVariants}>
              <img
                src={list3Img}
                alt="둘만 모여도 필요한 효율적인 구매 관리 | 이미지"
                title="둘만 모여도 필요한 효율적인 구매 관리"
              />
            </motion.div>
            <motion.div className="text" variants={yVariants}>
              <h3 className="title">
                둘만 모여도 필요한
                <br />
                효율적인 <span>구매 관리</span>
              </h3>
              <p className="desc">
                가족/친구/커플 등 2명이상, <br />
                공동의 물건 구매가 필요하다면
              </p>
            </motion.div>
          </motion.li>
        </ul>
      </div>
    </div>
  );
};

export default Section1;
